import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { CustomDropdownContainer, FormFieldLabel, PageHeader } from "CommonComponent";
import { hideCommonLoader, showCommonLoader } from "actions/commonActions";
import { useObjectViewedId } from "hooks/useObjectViewedId";
import {
  FREQUENCY_OPTIONS,
  MONTH_OPTIONS,
  PROBATION_UNIT_OPTIONS,
  STRATEGY_OPTIONS,
  emptyDeadlineOffsets,
  fetchGlobalSettings,
  getErrorMessage,
  normalizeDeadlineOffsets,
  updateGlobalSettings,
  validateDeadlineOffsetsClient,
} from "../pafEngineApi";
import "../PafEngine.css";

const notify = (message, type = "general_error") => ({
  type: "SHOW_SNACKBAR",
  snackbar_notification: message,
  snackbar_notification_type: type,
});

const optionByValue = (options, value) =>
  options.find((o) => o.value === value) || null;

/** Avoid clipping by .content-area overflow when menus open near the page bottom. */
const selectMenuProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  classNames: {
    menu: () => "paf-settings-select-menu",
  },
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  },
};

const PafEngineGlobalSettings = () => {
  const dispatch = useDispatch();
  const objectViewedId = useObjectViewedId();
  const [form, setForm] = useState({
    auto_generate_enabled: true,
    probation_period: 3,
    probation_unit: "month",
    frequency_strategies: {
      monthly: "company_schedule",
      quarterly: "company_schedule",
      yearly: "company_schedule",
    },
    deadline_offsets: emptyDeadlineOffsets(),
    company_monthly_day: 1,
    quarterly_start_month: "January",
    yearly_generation_month: "January",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    dispatch(showCommonLoader());
    try {
      const data = await fetchGlobalSettings(objectViewedId);
      setForm((prev) => ({
        ...prev,
        ...data,
        frequency_strategies: {
          monthly: data?.frequency_strategies?.monthly || "company_schedule",
          quarterly: data?.frequency_strategies?.quarterly || "company_schedule",
          yearly: data?.frequency_strategies?.yearly || "company_schedule",
        },
        deadline_offsets: normalizeDeadlineOffsets(data?.deadline_offsets),
      }));
      setFieldErrors({});
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  }, [dispatch, objectViewedId]);

  useEffect(() => {
    load();
  }, [load]);

  const setDeadlineOffset = (frequency, key, rawValue) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[`deadline_offsets.${frequency}.${key}`];
      return next;
    });
    setForm((prev) => ({
      ...prev,
      deadline_offsets: {
        ...prev.deadline_offsets,
        [frequency]: {
          ...prev.deadline_offsets?.[frequency],
          [key]: rawValue === "" || rawValue === null || rawValue === undefined
            ? ""
            : rawValue,
        },
      },
    }));
  };

  const save = async () => {
    const deadlinePayload = normalizeDeadlineOffsets(form.deadline_offsets);
    const clientErrors = validateDeadlineOffsetsClient(deadlinePayload);
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      dispatch(
        notify(
          Object.values(clientErrors)[0] ||
            "Please complete all deadline offset fields before saving."
        )
      );
      return;
    }

    setSaving(true);
    dispatch(showCommonLoader());
    try {
      const data = await updateGlobalSettings(
        {
          auto_generate_enabled: !!form.auto_generate_enabled,
          probation_period: Number(form.probation_period),
          probation_unit: form.probation_unit,
          frequency_strategies: form.frequency_strategies,
          generation_strategy: form.frequency_strategies?.monthly || "company_schedule",
          company_monthly_day: Number(form.company_monthly_day),
          quarterly_start_month: form.quarterly_start_month,
          yearly_generation_month: form.yearly_generation_month,
          deadline_offsets: deadlinePayload,
        },
        objectViewedId
      );
      setForm((prev) => ({
        ...prev,
        ...data,
        frequency_strategies: {
          monthly: data?.frequency_strategies?.monthly || prev.frequency_strategies.monthly,
          quarterly: data?.frequency_strategies?.quarterly || prev.frequency_strategies.quarterly,
          yearly: data?.frequency_strategies?.yearly || prev.frequency_strategies.yearly,
        },
        deadline_offsets: normalizeDeadlineOffsets(data?.deadline_offsets),
      }));
      setFieldErrors({});
      dispatch(notify("Global settings saved.", "success"));
    } catch (error) {
      const apiErrors = error?.response?.data?.errors;
      if (apiErrors && typeof apiErrors === "object") {
        const flattened = {};
        Object.entries(apiErrors).forEach(([key, messages]) => {
          flattened[key] = Array.isArray(messages) ? messages[0] : String(messages);
        });
        setFieldErrors(flattened);
      }
      dispatch(notify(getErrorMessage(error)));
    } finally {
      setSaving(false);
      dispatch(hideCommonLoader());
    }
  };

  const deadlineError = (frequency, key) =>
    fieldErrors[`deadline_offsets.${frequency}.${key}`] ||
    fieldErrors[`deadline_offsets.${frequency}`];

  return (
    <div className="paf-settings-page">
      <PageHeader title="Global PAF Settings">
        <Button className="n-u-btn" disabled={saving} onClick={save}>
          Save
        </Button>
      </PageHeader>

      <div className="main-content-div page-card">
        <div className="paf-engine-section-title">General</div>
        <Form.Check
          type="switch"
          id="auto-generate"
          label="Auto Generate PAFs"
          checked={!!form.auto_generate_enabled}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, auto_generate_enabled: e.target.checked }))
          }
        />

        <div className="paf-engine-section-title">Probation</div>
        <Row className="g-3 mb-2">
          <Col md={3}>
            <FormFieldLabel>Probation Period</FormFieldLabel>
            <Form.Control
              type="number"
              min={1}
              value={form.probation_period}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, probation_period: e.target.value }))
              }
            />
          </Col>
          <Col md={3}>
            <FormFieldLabel>Unit</FormFieldLabel>
            <Select
              className="multi-select"
              classNamePrefix="react-select"
              options={PROBATION_UNIT_OPTIONS}
              value={optionByValue(PROBATION_UNIT_OPTIONS, form.probation_unit)}
              onChange={(opt) =>
                setForm((prev) => ({ ...prev, probation_unit: opt?.value || "month" }))
              }
              components={{ ValueContainer: CustomDropdownContainer }}
              {...selectMenuProps}
            />
          </Col>
        </Row>

        <div className="paf-engine-section-title">Generation Strategy by Frequency</div>
        <Row className="g-3 mb-2">
          {FREQUENCY_OPTIONS.map((freq) => (
            <Col md={4} key={freq.value}>
              <FormFieldLabel>{freq.label}</FormFieldLabel>
              <Select
                className="multi-select"
                classNamePrefix="react-select"
                options={STRATEGY_OPTIONS}
                value={optionByValue(
                  STRATEGY_OPTIONS,
                  form.frequency_strategies?.[freq.value]
                )}
                onChange={(opt) =>
                  setForm((prev) => ({
                    ...prev,
                    frequency_strategies: {
                      ...prev.frequency_strategies,
                      [freq.value]: opt?.value || "company_schedule",
                    },
                  }))
                }
                components={{ ValueContainer: CustomDropdownContainer }}
                {...selectMenuProps}
              />
            </Col>
          ))}
        </Row>

        <div className="paf-engine-section-title">Company Schedule</div>
        <Row className="g-3">
          <Col md={3}>
            <FormFieldLabel>Monthly Generate On (Day)</FormFieldLabel>
            <Form.Control
              type="number"
              min={1}
              max={28}
              value={form.company_monthly_day}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, company_monthly_day: e.target.value }))
              }
            />
          </Col>
          <Col md={3}>
            <FormFieldLabel>Quarterly Start Month</FormFieldLabel>
            <Select
              className="multi-select"
              classNamePrefix="react-select"
              options={MONTH_OPTIONS}
              value={optionByValue(MONTH_OPTIONS, form.quarterly_start_month)}
              onChange={(opt) =>
                setForm((prev) => ({
                  ...prev,
                  quarterly_start_month: opt?.value || "January",
                }))
              }
              components={{ ValueContainer: CustomDropdownContainer }}
              {...selectMenuProps}
            />
          </Col>
          <Col md={3}>
            <FormFieldLabel>Yearly Generation Month</FormFieldLabel>
            <Select
              className="multi-select"
              classNamePrefix="react-select"
              options={MONTH_OPTIONS}
              value={optionByValue(MONTH_OPTIONS, form.yearly_generation_month)}
              onChange={(opt) =>
                setForm((prev) => ({
                  ...prev,
                  yearly_generation_month: opt?.value || "January",
                }))
              }
              components={{ ValueContainer: CustomDropdownContainer }}
              {...selectMenuProps}
            />
          </Col>
        </Row>

        <div className="paf-engine-section-title">Deadline Offsets</div>
        <p className="paf-engine-tagline">
          Deadline is calculated from the actual PAF generation date. Offsets are whole
          days after generation. Changing these values does not recalculate deadlines on
          existing PAFs. New PAFs will not generate until all offsets below are configured.
        </p>
        <p className="paf-engine-tagline">
          One-Level action deadline and Two-Level scoring deadline share the same stored
          scoring offset (days). Two-Level also requires a separate approval offset.
        </p>

        {FREQUENCY_OPTIONS.map((freq) => {
          const bucket = form.deadline_offsets?.[freq.value] || {
            scoring_offset_days: "",
            approval_offset_days: "",
          };
          const scoringErr = deadlineError(freq.value, "scoring_offset_days");
          const approvalErr = deadlineError(freq.value, "approval_offset_days");

          return (
            <div className="paf-engine-deadline-freq" key={freq.value}>
              <div className="paf-engine-deadline-freq-title">{freq.label}</div>

              <div className="paf-engine-deadline-workflow">
                <div className="paf-engine-deadline-workflow-title">One-Level</div>
                <p className="paf-engine-deadline-help">
                  Same person scores and approves. Action deadline equals scoring and
                  approval.
                </p>
                <Row className="g-3">
                  <Col md={4}>
                    <FormFieldLabel>Action Deadline (days)</FormFieldLabel>
                    <Form.Control
                      type="number"
                      min={0}
                      step={1}
                      value={
                        bucket.scoring_offset_days === null ||
                        bucket.scoring_offset_days === undefined
                          ? ""
                          : bucket.scoring_offset_days
                      }
                      isInvalid={!!scoringErr}
                      onChange={(e) =>
                        setDeadlineOffset(
                          freq.value,
                          "scoring_offset_days",
                          e.target.value
                        )
                      }
                    />
                    {scoringErr ? (
                      <Form.Control.Feedback type="invalid">
                        {scoringErr}
                      </Form.Control.Feedback>
                    ) : null}
                  </Col>
                </Row>
              </div>

              <div className="paf-engine-deadline-workflow">
                <div className="paf-engine-deadline-workflow-title">Two-Level</div>
                <p className="paf-engine-deadline-help">
                  Scorer and approver are different reporting levels. Scoring and approval
                  deadlines may differ.
                </p>
                <Row className="g-3">
                  <Col md={4}>
                    <FormFieldLabel>Scoring Deadline (days)</FormFieldLabel>
                    <Form.Control
                      type="number"
                      min={0}
                      step={1}
                      value={
                        bucket.scoring_offset_days === null ||
                        bucket.scoring_offset_days === undefined
                          ? ""
                          : bucket.scoring_offset_days
                      }
                      isInvalid={!!scoringErr}
                      onChange={(e) =>
                        setDeadlineOffset(
                          freq.value,
                          "scoring_offset_days",
                          e.target.value
                        )
                      }
                    />
                    {scoringErr ? (
                      <Form.Control.Feedback type="invalid">
                        {scoringErr}
                      </Form.Control.Feedback>
                    ) : null}
                  </Col>
                  <Col md={4}>
                    <FormFieldLabel>Approval Deadline (days)</FormFieldLabel>
                    <Form.Control
                      type="number"
                      min={0}
                      step={1}
                      value={
                        bucket.approval_offset_days === null ||
                        bucket.approval_offset_days === undefined
                          ? ""
                          : bucket.approval_offset_days
                      }
                      isInvalid={!!approvalErr}
                      onChange={(e) =>
                        setDeadlineOffset(
                          freq.value,
                          "approval_offset_days",
                          e.target.value
                        )
                      }
                    />
                    {approvalErr ? (
                      <Form.Control.Feedback type="invalid">
                        {approvalErr}
                      </Form.Control.Feedback>
                    ) : null}
                  </Col>
                </Row>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PafEngineGlobalSettings;
